/* ============================================================
   Temporadas

   O ranking que cresce para sempre não motiva ninguém: quem entrou
   atrasado nunca alcança, quem está na frente não tem o que perder. A
   temporada é a SEMANA, de segunda a domingo: toda segunda todo mundo
   volta para o zero, e a semana que fechou tem campeão.

   Os pontos da temporada saem da ficha, não dos pratos: o servidor já
   guarda os pontos de cada dia (`dayPoints`, com missão e custo de foto
   dentro) e os de cravada (`extraPoints`). Somar os dias da semana dá o
   mesmo número que o total soma — a tela, o fechamento e o perfil nunca
   discordam.
   ============================================================ */

import type { DayKey } from "./types.js";

const somar = (dia: string, n: number): string => {
  const [a, m, d] = dia.split("-").map(Number);
  return new Date(Date.UTC(a ?? 1970, (m ?? 1) - 1, (d ?? 1) + n)).toISOString().slice(0, 10);
};

/** A semana (segunda a domingo) que contém o dia. A chave é a segunda. */
export function semanaDe(dia: DayKey): { de: DayKey; ate: DayKey } {
  const [a, m, d] = dia.split("-").map(Number);
  const dow = new Date(Date.UTC(a ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay();
  const de = somar(dia, -((dow + 6) % 7));
  return { de, ate: somar(de, 6) };
}

/** A semana anterior à do dia — a que o fechamento de segunda fecha. */
export function semanaAnterior(dia: DayKey): { de: DayKey; ate: DayKey } {
  return semanaDe(somar(semanaDe(dia).de, -1));
}

/** O que o placar precisa de cada pessoa. */
export interface FichaDaTemporada {
  uid: string;
  name: string;
  photo?: string | null;
  days?: readonly DayKey[];
  dayPoints?: Record<DayKey, number>;
  extraPoints?: Record<DayKey, number>;
}

const r2 = (n: number) => Math.round(n * 100) / 100;

/** Pontos de uma pessoa entre `de` e `ate`, os dois inclusos. */
export function pontosNoPeriodo(f: FichaDaTemporada, de: DayKey, ate: DayKey): number {
  let total = 0;
  for (const mapa of [f.dayPoints ?? {}, f.extraPoints ?? {}]) {
    for (const [dia, pts] of Object.entries(mapa)) {
      if (dia >= de && dia <= ate && Number.isFinite(pts)) total += pts;
    }
  }
  return r2(total);
}

export interface LinhaDaTemporada {
  uid: string;
  name: string;
  photo: string;
  pontos: number;
  dias: number;
}

/**
 * O placar do período, do primeiro ao último. Empate em pontos desempata
 * por dias ativos (quem apareceu mais), depois pelo nome — nunca pela
 * ordem em que o banco devolveu, que muda entre uma leitura e outra.
 */
export function placarDoPeriodo(fichas: readonly FichaDaTemporada[], de: DayKey, ate: DayKey): LinhaDaTemporada[] {
  return fichas
    .map((f) => ({
      uid: f.uid, name: f.name, photo: f.photo ?? "",
      pontos: pontosNoPeriodo(f, de, ate),
      dias: (f.days ?? []).filter((d) => d >= de && d <= ate).length,
    }))
    .sort((a, b) => b.pontos - a.pontos || b.dias - a.dias || a.name.localeCompare(b.name, "pt-BR"));
}

/**
 * Quem ganhou. Ninguém, se ninguém pontuou — semana parada não tem
 * campeão, e coroar o primeiro da ordem alfabética seria mentira.
 * Empate no topo (pontos E dias) dá mais de um campeão.
 */
export function campeoesDo(placar: readonly LinhaDaTemporada[]): LinhaDaTemporada[] {
  const topo = placar[0];
  if (!topo || topo.pontos <= 0) return [];
  return placar.filter((l) => l.pontos === topo.pontos && l.dias === topo.dias);
}

/** "acaba em 2d 4h" — até a meia-noite de domingo, horário de Brasília. */
export function faltaParaAcabar(agora: Date, ate: DayKey): string {
  const [a, m, d] = ate.split("-").map(Number);
  // Meia-noite do dia seguinte ao `ate`, em Brasília (UTC−3).
  const fim = Date.UTC(a ?? 1970, (m ?? 1) - 1, (d ?? 1) + 1, 3, 0, 0);
  const ms = Math.max(0, fim - agora.getTime());
  const h = Math.floor(ms / 3600000);
  const dias = Math.floor(h / 24);
  if (dias >= 1) return `acaba em ${dias}d ${h % 24}h`;
  if (h >= 1) return `acaba em ${h}h`;
  return `acaba em ${Math.max(1, Math.floor(ms / 60000))} min`;
}

/**
 * A semana fechada que ainda "vale" hoje: a da semana passada. A coroa e
 * o cartão do campeão aparecem durante a semana seguinte e somem quando
 * ela fecha — campeão de duas semanas atrás não é mais notícia.
 */
export function temporadaVigente<T extends { de: DayKey; ate: DayKey; campeoes: string[] }>(
  temporadas: readonly T[], hoje: DayKey,
): T | null {
  const passada = semanaAnterior(hoje);
  return temporadas.find((t) => t.de === passada.de) ?? null;
}
